import React, { useState, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Paper, Tabs, Tab, Typography, IconButton, Alert, Button } from '@mui/material';
import { ArrowBack } from "@mui/icons-material";
import Grid from '@mui/material/Grid2';
import AppContext from '../components/AppContext.js'; 
import config from '../config.js';
import Saldo from '../components/common/Saldo.js';
import { SelectConcepte, fetchListConceptes } from '../components/common/SelectConcepte.js';
import DataLoader from '../components/common/DataLoader.js';
import AnualBalanceListGlobal from '../components/dashboard-anual/AnualBalanceListGlobal.js';
import TipusMovimentsList from '../components/common/TipusMovimentsList.js';
import useBack from '../hooks/useBack.js';
import { ConcepteGraphBanner, SubElementsList } from '../components/common/ConcepteGraphBanner.js';
import { resolveConcepteName, isNodeId, findNodeById, getResumGlobalNode } from '../services/concepteGraphService.js';

const DashboardConcepteGlobal = () => {
  const { ctxApp, setCtxApp, conceptesList: ctxConceptesList, conceptesTree: ctxConceptesTree } = useContext(AppContext);
  const [listConceptes, setListConceptes] = useState(null);
  const [tabValue, setTabValue] = useState(ctxApp?.dashboardConcepteGlobalTabSelected !== undefined ? ctxApp.dashboardConcepteGlobalTabSelected : 1);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const branchId1 = searchParams.get('id1');
  const branchId2 = searchParams.get('id2');
  const branchId3 = searchParams.get('id3');

  const { 
    userapp: paramUserapp, 
    concepte: paramConcepte,
    nodeId: paramNodeId } = useParams();

  const userapp = paramUserapp;

  // Distingir si naveguem per ID nou de node o ID antic de concepte
  const activeNodeId = paramNodeId || (isNodeId(paramConcepte) ? paramConcepte : null);
  const concepte = paramNodeId ? null : (isNodeId(paramConcepte) ? null : paramConcepte);
  const branchKey = `${branchId1 || ''}-${branchId2 || ''}-${branchId3 || ''}`;
  const id = (activeNodeId ? `node-${activeNodeId}-${branchKey}` : concepte);

  const { onBack } = useBack();

  const gotoBack = () => {
    onBack();
  }; 

  const handleConcepteChange = (selectedConcepte, selectedNode) => {
    if (selectedNode?.id && selectedNode.id > 0) {
      const branchParams = new URLSearchParams();
      if (selectedNode.id1) branchParams.set('id1', selectedNode.id1);
      if (selectedNode.id2) branchParams.set('id2', selectedNode.id2);
      if (selectedNode.id3) branchParams.set('id3', selectedNode.id3);
      const branchQuery = branchParams.toString() ? `?${branchParams.toString()}` : '';
      navigate(`/${userapp}/dashboard/node/${selectedNode.id}${branchQuery}`, { replace: true });
    } else {
      navigate(`/${userapp}/dashboard/concepte/${selectedConcepte}`, { replace: true });
    }
  };  
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setCtxApp(prev => ({ ...prev, dashboardConcepteGlobalTabSelected: newValue }));
  };

  const gotoDetail = (anio) => {
    setCtxApp(prev => ({ ...prev, dashboardConcepteAnualTabSelected: undefined }));
    if (activeNodeId) {
      const branchParams = new URLSearchParams();
      if (branchId1) branchParams.set('id1', branchId1);
      if (branchId2) branchParams.set('id2', branchId2);
      if (branchId3) branchParams.set('id3', branchId3);
      const branchQuery = branchParams.toString() ? `?${branchParams.toString()}` : '';
      navigate(`/${userapp}/dashboard/${anio}/node/${activeNodeId}${branchQuery}`);
    } else {
      navigate(`/${userapp}/dashboard/${anio}/concepte/${concepte}`);
    }
  };

  const fetchFunction = (jwtToken, onData, onError, onFinally) => {
    const allConceptes = listConceptes || ctxConceptesList || [];

    const buildEmptyData = (targetName, nodeObj = null) => ({
      concepte: { nom: targetName, tipus: nodeObj?.tipusFlux || 'DESPESA' },
      node: nodeObj,
      agregat: {
        importAgregat: { saldo: 0, ingressos: 0, despeses: 0, mitjaSaldo: 0, mitjaIngressos: 0, mitjaDespeses: 0 }
      },
      mapAgregatConceptes: {},
      mapAgregatMovimentsNomGlobal: {},
      senseDades: true
    });

    // MODE 1: NAVEGACIÓ PER ID NOU DE NODE DE GRAF
    if (activeNodeId) {
      const targetNodeIdNum = parseInt(String(activeNodeId).replace('node:', ''), 10);
      const treeNode = (ctxConceptesTree && targetNodeIdNum) ? findNodeById(ctxConceptesTree, targetNodeIdNum) : null;
      const id1 = branchId1 ? parseInt(branchId1, 10) : (treeNode?.id1 || null);
      const id2 = branchId2 ? parseInt(branchId2, 10) : (treeNode?.id2 || null);
      const id3 = branchId3 ? parseInt(branchId3, 10) : (treeNode?.id3 || null);
      const branchIds = { id1, id2, id3 };

      getResumGlobalNode(jwtToken, userapp, targetNodeIdNum, branchIds, true)
        .then((resData) => {
          if (!resData) {
            onData({ data: buildEmptyData(`Node #${targetNodeIdNum}`) });
            return;
          }
          const rawNode = resData.node;
          const resolvedTreeNode = treeNode || ((ctxConceptesTree && targetNodeIdNum) ? findNodeById(ctxConceptesTree, targetNodeIdNum) : null);
          const node = {
            ...resolvedTreeNode,
            ...rawNode,
            fills: (resolvedTreeNode?.fills && resolvedTreeNode.fills.length > 0) ? resolvedTreeNode.fills : (rawNode?.fills || [])
          };
          const agregat = resData.agregat || {
            importAgregat: { saldo: 0, ingressos: 0, despeses: 0, mitjaSaldo: 0, mitjaIngressos: 0, mitjaDespeses: 0 }
          };
          const mapAgregat = resData.mapAgregatConceptes || {};
          const mapMovs = resData.mapAgregatMovimentsNomGlobal || {};

          onData({
            data: {
              concepte: { nom: node?.nom || `Node #${targetNodeIdNum}`, tipus: node?.tipusFlux || 'DESPESA' },
              node: node,
              activeNodeId: targetNodeIdNum,
              conceptePath: node?.conceptePath || node?.path || '',
              agregat: agregat,
              mapAgregatConceptes: mapAgregat,
              mapAgregatMovimentsNomGlobal: mapMovs,
              senseDades: Object.keys(mapAgregat).length === 0 && (!agregat.importAgregat || (agregat.importAgregat.despeses === 0 && agregat.importAgregat.ingressos === 0))
            }
          });
        })
        .catch((err) => {
          console.error('Error carregant resum global del node:', err);
          onError(err, 'cargar datos del nodo');
        })
        .finally(() => onFinally());

      return;
    } else {
      // MODE 2: NAVEGACIÓ PER ID ANTIC CLÀSSIC (nom concepte)
      const resolvedName = resolveConcepteName(concepte, allConceptes, ctxConceptesTree) || concepte;

      axios.get(`${config.apiBaseUrl}/v1/resumglobalconcepte/${userapp}/${resolvedName}`, {
        headers: { Authorization: `${jwtToken}`}
      })
      .then((response) => {
        onData(response);
      })
      .catch((err) => {
        if (resolvedName !== resolvedName.toUpperCase()) {
          axios.get(`${config.apiBaseUrl}/v1/resumglobalconcepte/${userapp}/${resolvedName.toUpperCase()}`, {
            headers: { Authorization: `${jwtToken}`}
          })
          .then((response) => {
            onData(response);
          })
          .catch(() => {
            onData({ data: buildEmptyData(concepte) });
          });
        } else if (err.response?.status === 404 || err.response?.status === 400 || err.response?.status === 500) {
          onData({ data: buildEmptyData(resolvedName) });
        } else {
          onError(err, 'cargar datos');
        }
      })
      .finally(() => onFinally());
    }

    fetchListConceptes(jwtToken, 
      userapp,
      (data)=>{
        setListConceptes(data);
      },
      (err)=>{
        onError(err, 'cargar conceptes');
      }
    );
  };

  return (
    <DataLoader id={id} fetchFunction={fetchFunction} goBackLocation={`/${userapp}/dashboard`}>
      {(data) => {
        const isInversio = data.concepte?.tipus === 'INVERSIO';
        return (
        <>
          <Grid 
            size={{xs: 12, sm: 6 }} 
            sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}
          >
            <IconButton onClick={()=>gotoBack()}>
              <ArrowBack />
            </IconButton>

            <Typography variant="h5">
              Dashboard Concepte Global
            </Typography>
            
            <SelectConcepte
              label="Concepte"
              value={data.activeNodeId ? `node:${data.activeNodeId}` : data.concepte.nom}
              onChange={(e, node) => handleConcepteChange(e.target.value, node)}
              listConceptes={listConceptes} 
            />
          </Grid>

          {data.senseDades && (
            <Alert
              severity="info"
              sx={{ mt: 2, mb: 1 }}
              action={
                <Button color="inherit" size="small" onClick={() => gotoBack()}>
                  Tornar al Dashboard
                </Button>
              }
            >
              El concepte o node <strong>{data.concepte.nom}</strong> encara no té dades ni moviments associats a l'històric global.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ marginTop: '10px' }}>
            <Saldo titol="Saldo Concepte Global" 
              saldo={data.agregat.importAgregat.saldo} 
              ingressos={data.agregat.importAgregat.ingressos}
              despeses={data.agregat.importAgregat.despeses}
              size={{xs: 12, sm: 6, md: 4 }}
              mode={isInversio ? 'inversiones' : 'actual'}
            />

            <Saldo titol="Mitja Anual" 
              saldo={data.agregat.importAgregat.mitjaSaldo} 
              ingressos={data.agregat.importAgregat.mitjaIngressos}
              despeses={data.agregat.importAgregat.mitjaDespeses} 
              size={{xs: 12, sm: 6, md: 4 }}
              mode={isInversio ? 'inversiones' : 'actual'}
            />

          </Grid>

          <ConcepteGraphBanner 
            concepteNom={data.concepte.nom} 
            nodeId={data.activeNodeId}
            activeNode={data.node}
            userapp={userapp} 
          />

          <Grid container spacing={2} sx={{ marginTop: '10px' }}>
            <Grid size={12}>
            <Paper>
              <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
                <Tab label="Anys" />
                <Tab label={`Tipus Moviments (${Object.values(data.mapAgregatMovimentsNomGlobal).length})`} />
                <Tab label="Subelements / Branca" />
              </Tabs>

              {tabValue === 0 && (
                <AnualBalanceListGlobal years={data.mapAgregatConceptes} gotoDetail={gotoDetail} defaultViewMode={isInversio ? 'inversiones' : 'estalvi'}/>
              )}
              {tabValue === 1 && (
                <TipusMovimentsList tmovs={data.mapAgregatMovimentsNomGlobal}/>
              )}
              {tabValue === 2 && (
                <SubElementsList
                  concepteNom={data.concepte.nom}
                  nodeId={data.activeNodeId}
                  activeNode={data.node}
                  path={data.conceptePath}
                  userapp={userapp}
                />
              )}
            </Paper>

            </Grid>
          </Grid>
        </>
      );
    }}
    </DataLoader>
  );
};

export default DashboardConcepteGlobal;
